import {
    AfterViewInit,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewContainerRef
} from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { remove } from 'lodash';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { MonitoringSourcePickerFieldModel } from '../../models/monitoring-source-picker-field.model';
import { MonitoringSeriesTypeEnum } from '../../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { MonitoringBackEndDataSourceModel } from '../../models/monitoring-back-end-data-source.model';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { MonitoringRouteModel } from '../../models/monitoring-route.model';
import { RouteType } from '../../enums/route-type.enum';
import { MonitoringBulkActionSourceMethodEnum } from '../../enums/monitoring-bulk-action-source-method.enum';
import { MonitoringBulkActionTypeEnum } from '../../enums/monitoring-bulk-action-type.enum';
import { BulkMentionRequestModel } from '../../models/bulk-mention-request-model';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BulkActions } from './bulk-actions';
import { BulkActionConfigModel, BulkActionMessage } from '../../models/bulk-action-config.model';
import { LocalizeStringModel } from '../../../../Core/Localization/models/localize-string.model';
import { BulkActionFormComponent } from './bulk-action-form.component';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { SearchStatesEnum } from '../../enums/search-states.enum';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { MercuryApi } from '../../../../api/mercury/mercury-api';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import {
    BulkActionConfirmationDialogComponent
} from '../../../../Common/Dialogs/components/BulkActionConfirmationDialog/bulk-action-confirmation-dialog.component';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MentionSearchModel } from '../../models/mention-search.model';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { MentionDeleteDialogComponent } from '../MentionDeleteDialog/mention-delete-dialog.component';
import { FolderService } from '../../../Core/services/folder.service';
import { MentionsService } from '../../../Core/services/mentions.service';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { MentionSearchQueryModel } from '../../../../Monitor/Shared/models/mention-search-query.model';
import { ExportSearchCallerTypeEnum } from '@Common/MonitorShared/enum/export-search-caller-type.enum';
import { PostExportSetupDialogComponent } from '@Common/Dialogs/components/PostExportSetupDialog/post-export-setup-dialog.component';
import { AgprComponentStyleEnum } from '@Common/Basic/enums/social-analysis-types.enum';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { ExportFileTypeEnum } from '@Common/MonitorShared/enum/export-file-type.enum';
import { FileDownloadService } from '@Common/MonitorShared/services/file-downloader.service';
import { MentionTypeEnum } from '@Common/Mentions/components/UnsavedExportInfoMessage/unsaved-export-info-message.component';
import { SearchContextApiModel } from '../../models/search-context-api.model';


@Component({
    selector: 'agpr-monitoring-bulk-action-dialog',
    templateUrl: './monitoring-bulk-action-dialog.component.html',
    styleUrls: ['./monitoring-bulk-action-dialog.component.scss'],
    providers: [MentionSearchService, FileDownloadService],
    standalone: false
})
export class MonitoringBulkActionDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() mentionIds: string[] = [];
    @Input() mercuryIds: string[] = [];
    @Input() mentions: Mention[] = [];
    @Input() route: MonitoringRouteModel;
    @Input() search: MentionSearchModel;
    @Input() source: MonitoringBackEndDataSourceModel;
    @Input() dateQuery: DateQueryModel;

    @Input() set initialFilters(filters: MentionSearchCriteriaModel) {
        this.sourcePickerConfig.filterCriteria = filters;
    }

    @Output() changes: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('actionForm', { read: ViewContainerRef, static: true }) actionForm: ViewContainerRef;

    sourcePickerConfig: MonitoringSourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        null,
        'backendDataSource',
        '',
        '',
        {
            dataSourceLabel: 'Source',
            mentionCountLocalizationLabel: 'Monitoring.bulkActionDialog.sourcePickerField.resultFeedbackMessage',
            mentionCountLimitExceededLocalizationTitle: 'Monitoring.bulkActionDialog.sourcePickerField.tooManyResultsTitle',
            mentionCountLimitExceededLocalizationLabel: 'Monitoring.bulkActionDialog.sourcePickerField.tooManyResultsMessage',
            maxNumberOfMentions: 10000,
            dataSourceNullErrorMessage: 'Monitoring.sourcePickerField.dataSourceRequiredMessage',
            dataSourceOptions: [
                new DropdownOptionModel('Briefings.allSavedCoverage', MonitoringSeriesTypeEnum.allSavedCoverage),
                new DropdownOptionModel('Reports.series.type.folder', MonitoringSeriesTypeEnum.folder),
                new DropdownOptionModel('Monitoring.source.type.tag', MonitoringSeriesTypeEnum.tag),
                new DropdownOptionModel('Monitoring.source.type.tagGroup', MonitoringSeriesTypeEnum.tagGroup),
                new DropdownOptionModel('Monitoring.source.type.topic', MonitoringSeriesTypeEnum.topic),
                new DropdownOptionModel('Monitoring.source.type.sourceGroup', MonitoringSeriesTypeEnum.sourceGroup),
                new DropdownOptionModel('Monitoring.source.type.subscription', MonitoringSeriesTypeEnum.subscription),
                new DropdownOptionModel('Monitoring.source.type.aiAgent', MonitoringSeriesTypeEnum.aiAgent),
            ],
            showFilters: true
        }
    );
    sourcePickerDataSource: MonitoringBackEndDataSourceModel = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.folder);
    sourceMethod: MonitoringBulkActionSourceMethodEnum;

    actions = [
        ...BulkActions.map(action => {
            return {
                id: action.type,
                label: action.name,
                value: action
            };
        })
    ];
    actionDropdownOptions: DropdownOptionModel[];
    selectedAction: BulkActionConfigModel;
    selectedActionOption: DropdownOptionModel;

    actionValue: any = null;

    actionFormComponent: BulkActionFormComponent;

    loading: boolean = false;
    disableActionButton: boolean = false;
    fetchingMentions: boolean = false;
    mentionsCount: number = 0;
    combinedQuery: MentionSearchQueryModel;
    isUnsavedMentionType: boolean = false;
    unsavedMentionExportLimit: number = 0;
    unsavedMentionQuotaUsage: number = 0;
    remainingUnsavedMentionQuota: number = 0;
    totalMentionCount: number = 0;
    mentionType: MentionTypeEnum = MentionTypeEnum.MONITORING;

    overlayContainer: Element;

    private userPreferences: PreferencesModel;
    private subscriptions: Subscription[] = [];

    readonly id = 'monitoringBulkActionDialog';
    readonly maxExportMention: number = 50000;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly sourceType = MonitoringBulkActionSourceMethodEnum;
    readonly actionType = MonitoringBulkActionTypeEnum;
    readonly routeType = RouteType;
    readonly unsavedMentionsTypes: MonitoringSeriesTypeEnum[] = [
        MonitoringSeriesTypeEnum.currentSearch,
        MonitoringSeriesTypeEnum.topic,
        MonitoringSeriesTypeEnum.subscription,
    ];
    readonly unsavedMentionsRouteType: RouteType[] = [
        RouteType.topic,
        RouteType.results,
        RouteType.subscriptionResults,
        RouteType.topicAnalyticsSyndicates,
        RouteType.adHocAnalyticsSyndicates,
        RouteType.subscriptionAnalyticsSyndicates
    ];

    private readonly animationTrigger = new BehaviorSubject(
        new FullProfileAnimationModel(
            FullProfileDialogAnimationsEnum.slideInOutAnimation,
            OverlayAnimationStateEnum.open
        )
    );

    constructor(
        public coreDialogService: CoreDialogService,
        public searchService: MentionSearchService,
        public mentionsApi: MentionsApi,
        public currentUserStateService: CurrentUserStateService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public mercuryApi: MercuryApi,
        public currencyConversionService: CurrencyConversionService,
        public folderService: FolderService,
        public mentionsService: MentionsService,
        public fileDownloadService: FileDownloadService,
        private injector: Injector,
        public monitorConfigService: MonitorConfigService,
    ) {
        super(coreDialogService, 'MonitoringBulkAction');
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.userPreferences = preferences;
                }
            ),
            this.currentUserStateService.getPermissions().subscribe(
                permissions => {
                    const renormalizeActionIndex = this.actions.findIndex(
                        action => action.id === MonitoringBulkActionTypeEnum.renormalize
                    );

                    if (permissions?.includes(userPermissionsEnum.advancedMonitoringManager)) {
                        if (renormalizeActionIndex === -1) {
                            // find and add the renormalize action to the list of actions if it isn't present already
                            const renormalizeAction = BulkActions.find(action => action.type === MonitoringBulkActionTypeEnum.renormalize);
                            this.actions.push(
                                {
                                    id: renormalizeAction.type,
                                    label: renormalizeAction.name,
                                    value: renormalizeAction
                                }
                            );
                        }
                    } else {
                        // remove the renormalize action for non Adv. Monitoring accounts, if it's present
                        if (renormalizeActionIndex !== -1) {
                            this.actions.splice(renormalizeActionIndex, 1);
                        }
                    }
                    if ((!permissions?.includes(userPermissionsEnum.accountManager)
                        && !permissions?.includes(userPermissionsEnum.accountsReadOnlyManager))
                        || !permissions?.includes(userPermissionsEnum.copyMentionsToAccount)) {
                        this.actions = remove(this.actions, (action) => {
                            return action.value?.type !== MonitoringBulkActionTypeEnum.copyMentionsToAccount;
                        });
                    }
                    this.updateActionDropdownOptions();
                }
            ),
            this.searchService.getSearchState().subscribe(
                state => {
                    this.sourcePickerConfig.showLoadingFeedback = state.type === SearchStatesEnum.loading;
                    this.fetchingMentions = state.type === SearchStatesEnum.loading;
                    this.validateActionButton();
                }
            ),
            this.searchService.getMentionCount().subscribe(
                count => {
                    this.mentionsCount = count;
                    this.validateActionButton();
                }
            ),
            this.fileDownloadService.getUnsavedMentionExportLimit().subscribe(limit => {
                this.unsavedMentionExportLimit = limit;
            }),
            this.fileDownloadService.getUnsavedMentionQuotaUsage().subscribe(quotaUsage => {
                this.unsavedMentionQuotaUsage = quotaUsage;
            }),
            this.fileDownloadService.getRemainingUnsavedMentionQuota().subscribe(limit => {
                this.remainingUnsavedMentionQuota = limit;
                this.validateActionButton();
            }),
            this.searchService.getCombinedQuery().subscribe(query => this.combinedQuery = query)
        );
        this.updateActionDropdownOptions();
        this.selectedActionOption = this.actionDropdownOptions[0];
        this.searchService.setRequireCombinedQueryResults(true);
    }

    ngOnInit() {
        if ((this.mentionIds.length > 0) || (this.mercuryIds.length > 0)) {
            this.sourceMethod = MonitoringBulkActionSourceMethodEnum.SELECTION;
            this.disableActionButton = false;
        } else {
            this.sourceMethod = MonitoringBulkActionSourceMethodEnum.SEARCH;
        }
        switch (this.route.type) {
            case RouteType.folder:
            case RouteType.folderAnalyticsSyndicates:
                this.sourcePickerDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.folder);
                this.sourcePickerDataSource.id = +this.route.value;
                break;
            case RouteType.mentions:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
                this.sourcePickerDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.allSavedCoverage);
                break;
            case RouteType.results:
            case RouteType.adHocAnalyticsSyndicates:
                this.sourcePickerDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.currentSearch);
                // TODO: investigare whether we can remove combinedSearchCriteria with use of combinedQuery
                this.sourcePickerDataSource.combinedSearchCriteria = this.search?.searchCriteria?.clone();
                this.sourcePickerConfig.dataSourceOptions.push(
                    new DropdownOptionModel('Monitoring.source.type.searchResults', MonitoringSeriesTypeEnum.currentSearch)
                );
                break;
            case RouteType.topic:
            case RouteType.topicAnalyticsSyndicates:
                this.sourcePickerDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.topic);
                this.sourcePickerDataSource.combinedSearchCriteria = this.search?.searchCriteria.clone();
                this.sourcePickerDataSource.id = +this.route.value;
                break;
            case RouteType.subscriptionResults:
            case RouteType.subscriptionAnalyticsSyndicates:
                this.sourcePickerDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.subscription);
                this.sourcePickerDataSource.combinedSearchCriteria = this.search?.searchCriteria.clone();
                this.sourcePickerDataSource.id = +this.route.value;
                break;
            case RouteType.report:
                this.sourcePickerDataSource = this.source;
                this.sourcePickerConfig.filterCriteria = this.source.filterSearchCriteria;
                break;
            default:
                break;
        }
        this.updateActionDropdownOptions();
        if (this.selectedActionOption) {
            const selectedAction = this.actions.find(action =>
                action.label === this.selectedActionOption.label
            );
            if (selectedAction) {
                const matchingOption = this.actionDropdownOptions.find(option =>
                    option.label === selectedAction.label
                );
                if (matchingOption) {
                    this.selectedActionOption = matchingOption;
                    this.actionSelected(matchingOption.value);
                }
            }
        }
    }

    /**
     * Update the searchService with proper dateQuery
     */
    ngAfterViewInit() {
        this.searchService.setDateQuery(this.dateQuery);
    }

    isValidDataSource(): boolean {
        if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SEARCH && (
            !this.sourcePickerDataSource.combinedSearchCriteria || !this.combinedQuery)) {
            // no valid search criteria
            return false;
        } else if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SELECTION && (this.mentionIds.length === 0) && (this.mercuryIds.length === 0)) {
            // No mentions selected
            return false;
        }

        return true;
    }

    dataSourceChanged(event): void {
        this.sourcePickerConfig.showLoadingFeedback = true;
        this.updateActionDropdownOptions();
    }

    actionSelected(action: BulkActionConfigModel): void {
        this.selectedAction = action;

        if (this.selectedAction?.type === MonitoringBulkActionTypeEnum.export) {
            this.sourcePickerConfig.maxNumberOfMentions = 50000;
            this.selectedAction.buttonLabel = 'Monitoring.postFeed.bulkActions.export.header';
        } else if (this.selectedAction?.type === MonitoringBulkActionTypeEnum.copyMentionsToAccount){
            this.sourcePickerConfig.maxNumberOfMentions = 200;
        } else {
            this.sourcePickerConfig.maxNumberOfMentions = 10000;
        }

        if (this.selectedAction && this.actionFormComponent instanceof this.selectedAction.form) {
            // same type, don't reset or change value
            // however we might need to change the parameters, for example going between
            // add folder and remove folder there is a change despite it being the same component
            this.actionFormComponent.parameters = this.selectedAction.parameters;
            this.validateActionButton();
            return;
        }

        this.actionForm.clear();
        this.actionFormComponent = null;
        this.actionValue = null;

        if (this.selectedAction && this.selectedAction.form) {
            const componentRef = this.actionForm.createComponent<BulkActionFormComponent>(this.selectedAction.form);
            const formComponent = componentRef.instance;

            // Listen to changes first so that if the setter makes changes, we're listening
            formComponent.registerOnChange((value) => {
                this.actionValue = value;
                this.validateActionButton();
            });

            formComponent.action = this.selectedAction;
            formComponent.showErrors = false;
            formComponent.parameters = this.selectedAction.parameters;

            this.actionFormComponent = formComponent;
        }
        this.validateActionButton();
    }

    doAction() {
        this.sourcePickerConfig.showDataSourceError = !this.isValidDataSource();
        this.actionFormComponent.showErrors = !this.selectedAction.isValid(this.actionValue);

        if (this.sourcePickerConfig.showDataSourceError || this.actionFormComponent.showErrors) {
            return;
        }

        this.confirmAction();
    }

    /**
     * Validates if the action button is enabled or disabled
     */
    validateActionButton(): void {
        this.totalMentionCount = this.getNumberOfAffectedMentions();
        if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SELECTION) {
            this.disableActionButton = false;
        } else {
            this.disableActionButton = this.fetchingMentions ||
                this.totalMentionCount > this.sourcePickerConfig.maxNumberOfMentions ||
                this.totalMentionCount === 0;
        }

        if (!this.selectedAction || !this.selectedAction.isValid(this.actionValue)) {
            this.disableActionButton = true;
        }

        if (!this.fetchingMentions && this.selectedAction?.type === MonitoringBulkActionTypeEnum.export) {
            if (this.isUnsavedMentionType) {
                if (this.remainingUnsavedMentionQuota === 0) {
                    this.disableActionButton = true;
                }
                if (this.totalMentionCount > this.remainingUnsavedMentionQuota) {
                    this.disableActionButton = true;
                }
                if (this.unsavedMentionExportLimit === -1) {
                    this.disableActionButton = false;
                }
            }
            if (this.totalMentionCount > this.maxExportMention || this.totalMentionCount === 0) {
                this.disableActionButton = true;
            }
        }
    }

    /**
     * Gets the number of mentions being affected in the bulk action
     */
    getNumberOfAffectedMentions(): number {
        if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SELECTION) {
            if (this.isUnsavedMentionType) {
                return this.mercuryIds.length;
            }
            return this.mentionIds.length;
        }

        return this.mentionsCount;
    }

    /**
     * Exports selected content
     */
    private export(): void {
        let query = null;
        let searchContext = null;

        if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SEARCH) {
            const rawQuery = this.combinedQuery?.getApiEntity(false, true);
            // BE does not support both include and exclude at the same time
            // Keep include, clear exclude
            if (rawQuery?.mediaTypeFilter?.include?.length > 0 && rawQuery?.mediaTypeFilter?.exclude?.length > 0) {
                rawQuery.mediaTypeFilter = {
                    include: rawQuery.mediaTypeFilter.include,
                    exclude: []
                };
            }

            query = rawQuery;
        }

        let searchCallerType: ExportSearchCallerTypeEnum = ExportSearchCallerTypeEnum.FILE_EXPORT_SAVED_MENTION;

        if (this.isUnsavedMentionType) {
            searchCallerType = ExportSearchCallerTypeEnum.FILE_EXPORT_UNSAVED_MENTION;
        }

        let mentionIds = [];
        let mercuryIds = [];

        if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SELECTION) {
            if (this.isUnsavedMentionType) {
                mercuryIds = this.mercuryIds.slice();

                if (this.search?.searchCriteria?.query) {
                    const combinedQuery = this.search.searchCriteria.query.getApiEntity();
                    searchContext = {
                        advancedBooleanExpressionFilter: combinedQuery.advancedBooleanExpressionFilter,
                        caseSensitive: combinedQuery.caseSensitive,
                        keywordGroups: combinedQuery.keywordGroups
                    } as SearchContextApiModel;
                }
            } else {
                mentionIds = this.mentionIds.slice();
            }
        }

        const searchCriteria = this.searchService.generateFilterSearchCriteria();

        const io = {
            inputs: {
                fileName: 'Mentions - ' + moment().format('YYYY-MM-DD_HH-mm'),
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                style: AgprComponentStyleEnum.social,
                searchCallerType: searchCallerType,
                format: ExportFileTypeEnum.CSV,
                isUnsavedMentionType: this.isUnsavedMentionType,
                isSocial: false,
                query: query,
                searchContext: searchContext,
                mercuryIds: mercuryIds,
                mentionIds: mentionIds,
                searchCriteria: searchCriteria
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.closeDialog();
                    }
                }
            }
        };

        this.coreDialogService.open(PostExportSetupDialogComponent, io, this.injector);
    }

    private showActionMessage(message: BulkActionMessage): void {
        if (!message) {
            return;
        }

        this.messageService.add({
            severity: message.severity,
            summary: this.localizationService.transform(message.summary.key, message.summary.args),
            detail: this.localizationService.transform(message.detail.key, message.detail.args)
        });
    }

    /**
     * Performs bulk mention update
     */
    private update(numberOfAffectedMentions?: number): void {
        const accountSearchModel = this.sourcePickerDataSource.combinedSearchCriteria;
        accountSearchModel.query = this.combinedQuery;
        accountSearchModel.filterQuery = null;
        const modifications = this.selectedAction.prepareAction(this.actionValue);
        const bulkMentionRequest = new BulkMentionRequestModel(
            this.sourceMethod,
            modifications,
            accountSearchModel,
            this.mentionIds,
            this.selectedAction.type === MonitoringBulkActionTypeEnum.addTagsFromTaggingTopics,
            modifications.preserveEdit
        );
        this.loading = true;

        if (numberOfAffectedMentions == null || numberOfAffectedMentions === undefined) {
            numberOfAffectedMentions = this.getNumberOfAffectedMentions();
        }

        if (modifications.renormalize) {
            this.mentionsApi.updateMentionsInBulkAdmin(bulkMentionRequest.toApi()).pipe(take(1)).subscribe({
                next: () => {
                    this.loading = false;

                    this.showActionMessage(
                        this.selectedAction.actionSuccess(this.actionValue, numberOfAffectedMentions)
                    );
                    this.changes.emit();
                    this.closeDialog();
                },
                error: (error) => {
                    this.loading = false;

                    this.showActionMessage(
                        this.selectedAction.actionError(this.actionValue, numberOfAffectedMentions, error)
                    );
                }
            });
        } else if (this.selectedAction.type === MonitoringBulkActionTypeEnum.copyMentionsToAccount) {
            this.mentionsApi.copyMentionToAccount(bulkMentionRequest.copyFolderToAccountApi()).pipe(take(1)).subscribe({
                next: () => {
                    this.loading = false;

                    this.showActionMessage(
                        this.selectedAction.actionSuccess(this.actionValue, numberOfAffectedMentions)
                    );
                    this.changes.emit();
                    this.closeDialog();
                },
                error: (error) => {
                    this.loading = false;

                    this.showActionMessage(
                        this.selectedAction.actionError(this.actionValue, numberOfAffectedMentions, error)
                    );
                }
            });
        } else {
            this.mentionsApi.updateMentionsInBulk(bulkMentionRequest.toApi()).pipe(take(1)).subscribe({
                next: () => {
                    this.loading = false;

                    this.showActionMessage(
                        this.selectedAction.actionSuccess(this.actionValue, numberOfAffectedMentions)
                    );
                    this.changes.emit();
                    this.closeDialog();
                },
                error: (error) => {
                    this.loading = false;

                    this.showActionMessage(
                        this.selectedAction.actionError(this.actionValue, numberOfAffectedMentions, error)
                    );
                }
            });
        }
    }

    /**
     * Opens dialog to confirm action
     */
    private confirmAction(): void {
        if (this.selectedAction.type === MonitoringBulkActionTypeEnum.export) {
            this.export();
            return;
        }

        if (this.selectedAction.type === MonitoringBulkActionTypeEnum.deleteFromAccount) {
            this.openDeleteMentionsDialog();
            return;
        }

        const confirmMessage: LocalizeStringModel = this.selectedAction.confirmMessage(this.actionValue);
        if (!confirmMessage) {
            // if no confirmation provided, skip to update
            this.update();
            return;
        }

        this.showDefaultConfirmationDialog(confirmMessage);
    }

    /**
     * Displays confirmation dialog
     */
    private showDefaultConfirmationDialog(confirmMessage: LocalizeStringModel): void {
        const dialogIO = {
            inputs: {
                message: confirmMessage.key,
                messageParameters: confirmMessage.args,
                okLabel: this.selectedAction.buttonLabel,
                itemCount: this.getNumberOfAffectedMentions(),
                itemCountLabel: 'Monitoring.bulkActionDialog.numberOfMentionsSelected'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.update();
                    }
                }
            }
        };
        this.coreDialogService.open(BulkActionConfirmationDialogComponent, dialogIO);
    }

    /**
     * updates the action dropdown options based on actions
     */
    private updateActionDropdownOptions(): void {
        const actionDropdownLength = this.actionDropdownOptions?.length || 0;
        this.actionDropdownOptions = [];

        if (this.search?.searchCriteria?.isAccountSearch) {
            this.isUnsavedMentionType = false;
        } else {
            if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SELECTION) {
                this.isUnsavedMentionType = this.unsavedMentionsRouteType.includes(this.route?.type);
            } else {
                this.isUnsavedMentionType = this.unsavedMentionsTypes.includes(this.sourcePickerDataSource?.type);
            }
        }

        if (this.actions && this.actions.length > 0) {
            if (this.isUnsavedMentionType) {
                this.actions.forEach(action => {
                    if (action?.id === MonitoringBulkActionTypeEnum.export) {
                        this.actionDropdownOptions.push({
                            label: action.label,
                            value: action.value
                        });
                    } else if (!action.id){
                        this.actionDropdownOptions.push({
                            label: action.label,
                            value: action.value
                        });
                    }
                });
            } else {
                this.actions.forEach(action => {
                    this.actionDropdownOptions.push({
                        label: action.label,
                        value: action.value
                    });
                });
            }
        }

        if (actionDropdownLength !== this.actionDropdownOptions?.length) {
            this.actionForm?.clear();
            this.actionFormComponent = null;
            this.actionValue = null;
            this.selectedActionOption = this.actionDropdownOptions[0];
            // Select the new default action too, so its form is built - otherwise the old action stays
            // selected with no form behind it and the action button throws. The first pass runs from the
            // constructor, before the form container exists and before any action has been picked.
            if (this.actionForm) {
                this.actionSelected(this.selectedActionOption?.value);
            }
        }
    }

    openDeleteMentionsDialog(): void {
        const io: DialogIoModel = {
            inputs: {
                id: 'monitoringResult.deleteMentionDialog',
                dialogHeader: 'Monitoring.result.deleteMentionsHeader',
                dialogMessage: 'Monitoring.result.deleteMentionsHeadline',
                dialogConfirmMsg: 'Monitoring.result.deleteMentionsConfirmation',
                folderNames$: this.folderService.getListOfFoldersMentionsFoundIn(this.mentions),
                selectedMentions: this.mentions,
                itemCount: this.getNumberOfAffectedMentions(),
            },
            outputs: {
                dialogClose: (result: { closeAction: boolean; reason?: string }) => {
                    if (result.closeAction) {
                        // remove selected mentions from the current user account
                        const accountSearchModel = this.sourcePickerDataSource.combinedSearchCriteria;
                        accountSearchModel.query = this.combinedQuery;
                        this.mentionsService.removeMentionsFromAccount(
                            this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SELECTION ? this.mentions : [],
                            this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SEARCH ? accountSearchModel.query : undefined,
                            this.getNumberOfAffectedMentions(),
                            result.reason
                        ).subscribe({
                            next: () => {
                                this.changes.emit();
                                this.closeDialog();
                            }
                        });
                    }
                }
            }
        };

        if (this.sourceMethod === MonitoringBulkActionSourceMethodEnum.SEARCH) {
            delete io.inputs.selectedMentions;
            delete io.inputs.folderNames$;
        }

        this.coreDialogService.open(MentionDeleteDialogComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
